import { useEffect, useRef, useState } from "react";
import { Drawer } from "antd";
import { useAuthStore } from "../../store/auth/authStore";
import useIsMobile from "../../hooks/useIsMobile";
import TripsLogo from "../../assets/images/trips-logo.svg";
import {
  ModalContainer,
  StyledModal,
  StyledInput,
  LogoContainer,
  Title,
  SocialButton,
  DividerContainer,
  FormContainer,
  InputLabel,
  DisclaimerText,
  SubmitButton,
  TermsText,
  OTPContainer,
  ResendText,
  Spinner,
  ResendTextButton,
  LoadingOtpContainer,
  OtpContainer,
  LastLoginText,
  TermsLink,
  OtpError,
  OtpInput,
} from "./styles";
import { ReactComponent as GoogleIcon } from "../../assets/images/google-icon.svg";
import {
  LOGIN_URL,
  VERIFY_OTP_URL,
  RESEND_OTP_URL,
  GOOGLE_LOGIN,
  GOOGLE_CALLBACK_URL,
} from "../../utils/constants";
import { encryptedPostAPI, getAPICustom } from "../../utils/api";
import { EncryptionHelper } from "../../utils/encryption";
import AlertMessage from "../AlertMessage";
import OTPInput from "react-otp-input";
const RESEND_TIMEOUT = 60;

export default function AuthModals() {
  const { isMobile } = useIsMobile();
  const {
    showAuthModal,
    showOTPModal,
    setAuthModal,
    setOTPModal,
    login,
    setB2cAuth,
    setLastLoginMethod,
    lastLoginMethod,
  } = useAuthStore();
  const [localEmail, setLocalEmail] = useState("");
  const auth = localStorage.getItem("trips_auth");
  const obj: UserAuth = auth ? JSON.parse(auth) : null;
  const merchantHash = obj?.Hash;
  const merchantCode = obj?.MerchantCode;
  const merchantToken = obj?.Token;
  const merchantName = obj?.MerchantName;
  const [requesting, setRequesting] = useState(false);
  const [signinMethod, setSignInMethod] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [timeLeft, setTimeLeft] = useState(RESEND_TIMEOUT);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [otp, setOtp] = useState("");

  const startTimer = () => {
    setTimeLeft(RESEND_TIMEOUT);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    startTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleEmailSubmit = async () => {
    setErrorMessage("");
    setSignInMethod("email");
    setRequesting(true);
    const loginURL = LOGIN_URL;
    const stringPayload = JSON.stringify({ Email: localEmail });
    const encryptedPayload = await EncryptionHelper.encrypt(
      stringPayload,
      merchantHash,
      merchantCode,
    );
    try {
      let loginResponse = await encryptedPostAPI(loginURL, encryptedPayload);
      if (loginResponse.status === 200) {
        setAuthModal(false);
        startTimer();
        setOTPModal(true);
      } else {
        setErrorMessage(
          loginResponse?.data?.Message || "Failed to send verification code",
        );
      }
    } catch (error: any) {
      setErrorMessage(
        error?.data?.Message ||
          error?.message ||
          "Failed to send verification code",
      );
    } finally {
      setRequesting(false);
    }
  };

  const handleGoogleSignin = async () => {
    setErrorMessage("");
    setRequesting(true);
    setSignInMethod("google");

    const googleLoginURL = GOOGLE_LOGIN;
    const Payload = {
      ReturnUrl: `${window.location.origin}${GOOGLE_CALLBACK_URL}`,
    };

    try {
      let googleLoginResponse = await getAPICustom(googleLoginURL, Payload, {
        Authorization: `Bearer ${merchantToken || ""}`,
        MerchantCode: merchantCode,
        AppName: merchantName,
        MerchantHash: merchantHash,
      });

      if (googleLoginResponse.status === 200) {
        setLastLoginMethod("google");
        window.location.href = googleLoginResponse?.data?.googleAuthUrl;
      } else {
        setErrorMessage(
          googleLoginResponse?.data?.Message ||
            "Failed to initiate Google sign in process",
        );
      }
    } catch (error: any) {
      setErrorMessage(
        error?.data?.Message ||
          error?.message ||
          "Failed to initiate Google sign in process",
      );
    } finally {
      setRequesting(false);
    }
  };

  const resendOtp = async () => {
    setResendingOtp(true);
    setErrorMessage("");
    const resendUrl = RESEND_OTP_URL;
    const stringPayload = JSON.stringify({ Email: localEmail });
    const encryptedPayload = await EncryptionHelper.encrypt(
      stringPayload,
      merchantHash,
      merchantCode,
    );
    try {
      let resendResponse = await encryptedPostAPI(resendUrl, encryptedPayload);
      console.log("resendResponse", resendResponse);

      if (resendResponse.status === 200) {
        startTimer();
      } else {
        setErrorMessage(
          resendResponse?.data?.Message || "Failed to send verification code",
        );
      }
    } catch (error: any) {
      setErrorMessage(
        error?.data?.Message ||
          error?.message ||
          "Failed to resend verification code",
      );
    } finally {
      setResendingOtp(false);
    }
  };

  const handleOtpChange = async (value: string) => {
    setErrorMessage("");
    setOtp(value);
    if (value.length !== 6) {
      return;
    } else {
      setRequesting(true);
      const verifyOtpURL = VERIFY_OTP_URL;
      const extractedOtp = value;
      const stringPayload = JSON.stringify({
        Email: localEmail,
        Otp: extractedOtp,
      });
      const encryptedPayload = await EncryptionHelper.encrypt(
        stringPayload,
        merchantHash,
        merchantCode,
      );
      try {
        let loginResponse = await encryptedPostAPI(
          verifyOtpURL,
          encryptedPayload,
        );
        if (loginResponse.status === 200) {
          setOTPModal(true);
          setLastLoginMethod("email");
          const decryptedData = await EncryptionHelper.decrypt(
            loginResponse?.data,
            merchantHash,
            merchantCode,
          );
          const parsedDecryptedData = JSON.parse(decryptedData);

          const { AccessToken, User } = parsedDecryptedData;
          // Setting auth details
          setB2cAuth(AccessToken, User);
          setLocalEmail("");
          setOtp("");
          setErrorMessage("");
          login();
        } else {
          setErrorMessage(
            loginResponse?.data?.Message || "Failed to send verification code",
          );
        }
      } catch (error: any) {
        setErrorMessage(
          error?.data?.Message || "Failed to send verification code",
        );
      } finally {
        setRequesting(false);
      }
    }
  };

  const renderAuthContent = () => (
    <ModalContainer>
      <LogoContainer>
        <img src={TripsLogo} alt="TRIPS" height="30" />
      </LogoContainer>
      <Title>Log in or sign up</Title>

      {errorMessage && (
        <AlertMessage
          fullWidth={true}
          message={errorMessage}
          closable={false}
        />
      )}

      <SocialButton
        style={{ position: "relative" }}
        onClick={handleGoogleSignin}
        disabled={requesting && signinMethod === "google"}>
        {lastLoginMethod !== "google" && (
          <LastLoginText>Last used</LastLoginText>
        )}
        {requesting && signinMethod === "google" ? (
          <Spinner variant="white" />
        ) : (
          <>
            <GoogleIcon /> Continue with google
          </>
        )}
      </SocialButton>

      {/* <SocialButton>
        <AppleFilled /> Continue with apple
      </SocialButton> */}

      <DividerContainer>
        <span>OR</span>
      </DividerContainer>

      <FormContainer>
        <InputLabel>Email address</InputLabel>
        <StyledInput
          size="large"
          value={localEmail}
          onChange={(e) => setLocalEmail(e.target.value)}
        />
        <DisclaimerText>
          We sent a temporary code to verify this email
        </DisclaimerText>

        <SubmitButton onClick={handleEmailSubmit} disabled={requesting}>
          {requesting && signinMethod === "email" ? (
            <Spinner variant="white" />
          ) : (
            "Continue"
          )}
        </SubmitButton>

        <TermsText>
          By continuing you accept the{" "}
          <TermsLink href="#">Terms of Use</TermsLink> and{" "}
          <TermsLink href="#">Privacy Policy</TermsLink>
        </TermsText>
      </FormContainer>
    </ModalContainer>
  );

  const renderOTPContent = () => (
    <ModalContainer>
      <LogoContainer>
        <img src={TripsLogo} alt="TRIPS" height="32" />
      </LogoContainer>
      <Title style={{ marginBottom: "0" }}>Verify your email</Title>

      <div
        style={{ textAlign: "center", marginBottom: "32px", color: "#4B5563" }}>
        We sent a temporary code to <strong>{localEmail}</strong>
      </div>
      {errorMessage && (
        <AlertMessage
          fullWidth={true}
          message={errorMessage}
          closable={false}
        />
      )}
      <OtpContainer>
        {requesting && (
          <LoadingOtpContainer>
            <Spinner size={40} />
          </LoadingOtpContainer>
        )}
        <OTPContainer>
          <OTPInput
            value={otp}
            onChange={handleOtpChange}
            numInputs={6}
            inputType="tel"
            renderInput={(props: any) => <OtpInput {...props} />}
            inputStyle={{
              width: isMobile ? "40px" : "50px",
              height: isMobile ? "48px" : "58px",
              margin: isMobile ? "0 4px" : "0 5px",
              fontSize: "18px",
              fontWeight: "500",
              borderRadius: isMobile ? "10px" : "20px",
              border: "1px solid #e4e4e7",
              outline: "none",
              textAlign: "center",
            }}
          />
        </OTPContainer>

        {timeLeft > 0 ? (
          <ResendText>Resend in {timeLeft} secs</ResendText>
        ) : (
          <ResendTextButton onClick={resendOtp} disabled={resendingOtp}>
            {resendingOtp ? "Resending..." : "Resend"}
          </ResendTextButton>
        )}
      </OtpContainer>
    </ModalContainer>
  );

  if (isMobile) {
    return (
      <>
        <Drawer
          placement="bottom"
          open={showAuthModal}
          onClose={() => setAuthModal(false)}
          height="auto"
          closable={true}
          style={{ borderTopLeftRadius: "24px", borderTopRightRadius: "24px" }}>
          {renderAuthContent()}
        </Drawer>

        <Drawer
          placement="bottom"
          open={showOTPModal}
          onClose={() => setOTPModal(false)}
          height="auto"
          closable={true}
          style={{ borderTopLeftRadius: "24px", borderTopRightRadius: "24px" }}>
          {renderOTPContent()}
        </Drawer>
      </>
    );
  }

  return (
    <>
      <StyledModal
        open={showAuthModal}
        onCancel={() => {
          setAuthModal(false);
          setErrorMessage("");
          setOtp("");
          setLocalEmail("");
        }}
        footer={null}
        width={520}
        centered
        closable={true}
        bodyStyle={{ padding: "0" }}>
        {renderAuthContent()}
      </StyledModal>

      <StyledModal
        open={showOTPModal}
        onCancel={() => {
          setOTPModal(false);
          setErrorMessage("");
          setOtp("");
          setLocalEmail("");
        }}
        footer={null}
        width={480}
        centered
        closable={true}
        bodyStyle={{ padding: "32px 24px" }}>
        {renderOTPContent()}
      </StyledModal>
    </>
  );
}
